
















































































import { Component, Inject, Mixins, Watch } from "vue-property-decorator";
import { ActionTypes } from "@/data/enums";
import CountriesStore from "@/mixins/constants/countriesStore";

import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import { IClientAddress } from "@/models/clients";
import { DataModules } from "@/store/modules/data";
import { mapState } from "vuex";
import RequestPromises from "@/mixins/requestPromises";

@Component({
  components: {
    "address-search": () =>
      import("@/components/app/global/address/addressSearch.vue")
  },
  computed: {
    ...mapState({
      dataScope(state, getters) {
        return getters[`data/${DataModules.CLIENTS_ADDRESSES}/clientScope`](
          this.basketData.clientId
        );
      },
      clientAddresses(state, getters) {
        return this.$_.orderBy(
          getters[`data/listArray`]({
            storeModule: DataModules.CLIENTS_ADDRESSES,
            scope: this.dataScope
          }),
          ["created_at", "asc"]
        );
      }
    })
  }
})
export default class BasketAddresses extends Mixins(
  RequestPromises,
  CountriesStore
) {
  // Inject
  @Inject({ from: "providerData" })
  public readonly $providerData!: () => IBasketProviderData;
  @Inject({ from: "providerMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  // Data
  public actionTypes = ActionTypes;
  public addingAddress = false;
  public addressId = this.basketAddressId;
  public addressSearchedKey = this.$uuid();
  public clientAddresses!: IClientAddress[];
  public dataScope!: string;
  public isLoading = true;
  public searchedValue = "";

  // Computed
  public get basketData() {
    return this.$providerData();
  }

  public get basketAddressId() {
    return this.$_.get(this.basketData, "basket.address_id");
  }

  public get basketCompanyId() {
    return this.$_.get(this.basketData, "basket.company_id");
  }

  // Life-cycle methods
  public async created() {
    try {
      await this.getClientAddresses();
    } finally {
      this.isLoading = false;
    }
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CLIENTS_ADDRESSES,
      scope: this.dataScope,
      vm: this
    });
  }

  // Methods
  public getClientAddresses() {
    return this.$store.dispatch(`data/${DataModules.CLIENTS_ADDRESSES}/list`, {
      id: this.basketData.clientId,
      scope: this.dataScope,
      ignoreStored: true,
      vm: this
    });
  }

  public async onAddressSearchResult(address, searchedValue) {
    this.openAddAddressModal({
      address: {
        ...address,
        name: address.address_1,
        type: 1,
        default: true
      },
      actionType: ActionTypes.CREATE,
      searchedValue
    });
  }

  public openAddAddressModal({
    address = {} as IClientAddress,
    actionType = ActionTypes.UPDATE,
    searchedValue = ""
  }: {
    address: IClientAddress;
    actionType: ActionTypes;
    searchedValue: string;
  }) {
    this.addingAddress = true;
    this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import(
            "@/components/app/global/client/addEditClientAddressModal.vue"
          ),
        props: this.$_.merge(
          { clientId: this.basketData.clientId },
          this.$_.isEmpty(address)
            ? {}
            : {
                data: this.$_.pick(address, [
                  "name",
                  "address_1",
                  "address_2",
                  "country_id",
                  "region_id",
                  "city",
                  "postcode",
                  "type"
                ]),
                actionType,
                addressId:
                  actionType === ActionTypes.UPDATE ? address.id : undefined
              },
          searchedValue ? { searchedValue } : {}
        ),
        width: 640,
        onCancel: () => {
          this.clearAddressSearch();
        },
        events: {
          success: async (address: IClientAddress) => {
            // Add or update address in store
            this.$store.commit("data/addSingleItemList", {
              storeModule: DataModules.CLIENTS_ADDRESSES,
              scope: this.dataScope,
              path: `data.${address.id}`,
              data: {
                ...address,
                _order: this.$_.get(
                  this.$_.find(this.clientAddresses, ["id", address.id]),
                  "_order",
                  this.clientAddresses.length
                )
              }
            });
            if (actionType === ActionTypes.CREATE) {
              // Change ID, triggering updateBasketAddress
              this.addressId = address.id;
            } else if (this.basketAddressId === address.id) {
              // Reload basket incase VAT amount changed
              await this.$basketMethods.getBasket(true);
            }
            this.clearAddressSearch();
          }
        }
      }
    });
  }

  public clearAddressSearch() {
    this.addingAddress = false;
    this.$set(this, "searchedValue", "");
    this.addressSearchedKey = this.$uuid();
  }

  @Watch("clientAddresses.length")
  public onAddressesChange(count, oldCount) {
    if (!this.addressId && !oldCount) {
      // Select default company
      this.addressId = this.$_.get(
        this.$_.find(this.clientAddresses, ["default", true]),
        "id",
        null
      );
    }
  }

  @Watch("addressId")
  public async updateBasketAddress(addressId = undefined) {
    try {
      if (this.basketAddressId !== addressId)
        await this.$basketMethods.updateBasketData({
          address_id: this.addressId,
          company_id: null
        });
      // Reload basket incase VAT amount changed
      await this.$basketMethods.getBasket(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  @Watch("basketCompanyId", { immediate: true })
  public deselectCompany(companyId) {
    if (companyId) this.updateBasketAddress();
  }
}
